import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash, faSpinner, faShield } from '@fortawesome/free-solid-svg-icons';
import {
  warrantyTypeService,
  type WarrantyType,
  type CreateWarrantyTypePayload,
} from '@/services/warranty-type.service';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';

const PAGE_SIZE = 20;

interface WarrantyTypeFormProps {
  initial?: Partial<CreateWarrantyTypePayload>;
  onSubmit: (d: CreateWarrantyTypePayload) => void;
  onCancel: () => void;
  saving: boolean;
}

function WarrantyTypeForm({ initial, onSubmit, onCancel, saving }: WarrantyTypeFormProps) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [coverageDetails, setCoverageDetails] = useState(initial?.coverageDetails ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!code.trim()) errs.code = 'Code is required';
    if (!name.trim()) errs.name = 'Name is required';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    onSubmit({
      code: code.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
      coverageDetails: coverageDetails.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField label="Code" htmlFor="wt-code" required error={errors.code}>
        <Input id="wt-code" value={code} onChange={(e) => setCode(e.target.value)}
          placeholder="e.g. MFR, EXT, SVC" disabled={saving} maxLength={50} />
      </FormField>
      <FormField label="Name" htmlFor="wt-name" required error={errors.name}>
        <Input id="wt-name" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Manufacturer's Warranty" disabled={saving} maxLength={200} />
      </FormField>
      <FormField label="Description" htmlFor="wt-desc">
        <Textarea id="wt-desc" value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional short description" rows={2} disabled={saving} />
      </FormField>
      <FormField label="Coverage Details" htmlFor="wt-coverage">
        <Textarea id="wt-coverage" value={coverageDetails} onChange={(e) => setCoverageDetails(e.target.value)}
          placeholder="Describe what is and is not covered..." rows={4} disabled={saving} />
      </FormField>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="submit" disabled={saving} className="min-w-[100px]">
          {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Save'}
        </Button>
      </div>
    </form>
  );
}

export function WarrantyTypesPage() {
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<WarrantyType | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['warranty-types', page, debouncedSearch],
    queryFn: () => warrantyTypeService.list({ page, pageSize: PAGE_SIZE, search: debouncedSearch || undefined }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['warranty-types'] });

  const createMutation = useMutation({
    mutationFn: warrantyTypeService.create,
    onSuccess: () => { toast.success('Warranty type created'); setModalOpen(false); invalidate(); },
    onError: () => toast.error('Failed to create warranty type'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreateWarrantyTypePayload> }) =>
      warrantyTypeService.update(id, payload),
    onSuccess: () => { toast.success('Warranty type updated'); setModalOpen(false); setEditTarget(null); invalidate(); },
    onError: () => toast.error('Failed to update warranty type'),
  });

  const deleteMutation = useMutation({
    mutationFn: warrantyTypeService.remove,
    onSuccess: () => { toast.success('Warranty type deleted'); invalidate(); },
    onError: () => toast.error('Failed to delete warranty type'),
  });

  const handleDelete = async (t: WarrantyType) => {
    if (await confirmDelete(`${t.code} — ${t.name}`)) deleteMutation.mutate(t.id);
  };

  const openCreate = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (t: WarrantyType) => { setEditTarget(t); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditTarget(null); };
  const handleSearch = (v: string) => { setSearch(v); setPage(1); };
  const saving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) return <SkeletonPage />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Warranty Types"
        description="Define warranty categories and their coverage details for equipment records"
        onAdd={can('warranty-type:create') ? openCreate : undefined}
        addLabel="Add Warranty Type"
      />
      <SearchBar value={search} onChange={handleSearch} placeholder="Search by code or name..." />

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[620px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">Code</th>
                  <th className="px-4 py-3 font-medium text-foreground">Name</th>
                  <th className="px-4 py-3 font-medium text-foreground">Description</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((t) => (
                  <tr key={t.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-3 font-mono font-medium text-foreground">{t.code}</td>
                    <td className="px-4 py-3 font-medium text-foreground">{t.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{t.description ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${t.isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {t.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {can('warranty-type:update') && (
                          <button onClick={() => openEdit(t)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label={`Edit ${t.name}`}>
                            <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {can('warranty-type:delete') && (
                          <button onClick={() => handleDelete(t)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            aria-label={`Delete ${t.name}`} disabled={deleteMutation.isPending}>
                            <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />
        </>
      ) : (
        <EmptyState
          icon={faShield}
          title={search ? 'No warranty types match your search' : 'No warranty types yet'}
          description={search ? 'Try a different search term.' : "Add warranty categories such as Manufacturer's Warranty, Extended Service Contract, or No Warranty."}
          action={!search && can('warranty-type:create') ? <Button onClick={openCreate}>Add Warranty Type</Button> : undefined}
        />
      )}

      <Modal open={modalOpen} onClose={closeModal} title={editTarget ? 'Edit Warranty Type' : 'Add Warranty Type'} size="md">
        <WarrantyTypeForm
          initial={editTarget ? { code: editTarget.code, name: editTarget.name, description: editTarget.description ?? undefined, coverageDetails: editTarget.coverageDetails ?? undefined } : undefined}
          onSubmit={(p) => editTarget ? updateMutation.mutate({ id: editTarget.id, payload: p }) : createMutation.mutate(p)}
          onCancel={closeModal}
          saving={saving}
        />
      </Modal>
    </div>
  );
}
